import React, { useMemo, useState, useCallback, type ReactNode } from 'react';
// eslint-disable-next-line @atlaskit/design-system/no-unsupported-drag-and-drop-libraries
import { type DropResult, DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import FocusRing from '@atlaskit/focus-ring';
import DragHandlerIcon from '@atlaskit/icon/glyph/drag-handler';
import Spinner from '@atlaskit/spinner';
import PageHeader from '@atlassian/jira-common-components-page-header';
import { JSErrorBoundary } from '@atlassian/jira-error-boundaries/src/ui/js-error-boundary/index.tsx';
import { useFlagService } from '@atlassian/jira-flags';
import { useIntl } from '@atlassian/jira-intl';
import { ContextualAnalyticsData, fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import { JsmPageInteractiveEvent } from '@atlassian/jira-servicedesk-common/src/utils/browser-metric/index.tsx';
import { usePageContext } from '@atlassian/jira-servicedesk-spa-commons/src/common/utils/page-context/main.tsx';
import { MAX_NO_OF_LINKS_ALLOWED, MODES } from '../../common/constants';
import type { ExternalLinkObject } from '../../common/types';
import { getTruncatedTitleForFlag } from '../../common/utils';
import { markReorderExperienceFailed } from '../../experiences';
import { AddLinkModal } from './add-link-modal/main.tsx';
import { DeleteDiscardPrompt } from './delete-discard-prompt';
import { EmptyLinkSection } from './empty-link-section';
import { LimitReachedPopup } from './limit-reached-popup';
import { Link } from './link';
import messages from './messages';
import {
	LinksWrapper,
	LinksContentContainer,
	SpinnerContainer,
	PageActionsWrapper,
	StyledButton,
	LinkWrapper,
	LinkContentWrapper,
	DragWrapper,
	DragButton,
	LinksListWrapper,
} from './styled';
import type { LinksProps } from './types';

export const Links = ({ links, isLoading, isDeleting, onDelete, onReorder }: LinksProps) => {
	const [linkToBeDeleted, setIsLinkToBeDeleted] = useState<unknown>(null);
	const { formatMessage } = useIntl();
	const { showFlag } = useFlagService();
	const hasLinks = links.length > 0;
	const isMaxLimitReached = links.length >= MAX_NO_OF_LINKS_ALLOWED;

	const [isAddLinkModalOpen, setIsAddLinkModalOpen] = useState(false);
	const [mode, setMode] = useState<'ADD' | 'EDIT'>(MODES.ADD);
	const [linkToBeUpdated, setLinkToBeUpdated] = useState<unknown>(null);

	const [{ data: pageContextData }] = usePageContext();

	const portalUrl = pageContextData?.serviceDeskContext.portalUrl;

	const openAddLinkModal = useCallback(() => {
		setMode(MODES.ADD);
		setIsAddLinkModalOpen(true);
	}, [setIsAddLinkModalOpen, setMode]);

	const closeAddLinkModal = useCallback(
		() => setIsAddLinkModalOpen(false),
		[setIsAddLinkModalOpen],
	);

	const openUpdateLinkModal = useCallback(
		(linkId: string) => {
			setMode(MODES.EDIT);
			setIsAddLinkModalOpen(true);
			setLinkToBeUpdated(links[links.findIndex(({ id }) => id === linkId)]);
		},
		[setIsAddLinkModalOpen, setMode, setLinkToBeUpdated, links],
	);

	const onDragEnd = useCallback(
		(result: DropResult) => {
			const sourceIndex = result.source.index;
			const destinationIndex = result.destination ? result.destination.index : null;

			// may not have any destination (drag to nowhere)
			if (destinationIndex !== null) {
				onReorder(sourceIndex, destinationIndex).catch(() => {
					showFlag({
						type: 'error',
						isAutoDismiss: true,
						title: messages.externalLinksReorderFailureTitle,
						description: messages.externalLinksReorderFailureDescription,
					});
				});
			}
		},
		[onReorder, showFlag],
	);

	const DraggableLinks = useMemo(
		() =>
			links.map((link, index) => (
				<Draggable draggableId={link.id} key={link.id} index={index}>
					{({ draggableProps, dragHandleProps, innerRef }, { isDragging }) => (
						<DragWrapper {...draggableProps} ref={innerRef} isDragging={isDragging}>
							<LinkWrapper data-testid="servicedesk-external-links.ui.links.link-wrapper">
								<FocusRing isInset>
									<DragButton
										{...dragHandleProps}
										aria-label={formatMessage(messages.externalLinksReorderDragIndicatorNonfinal)}
										data-testid="servicedesk-external-links.ui.links.drag-button"
									>
										<DragHandlerIcon
											label={formatMessage(messages.externalLinksReorderDragIndicatorNonfinal)}
										/>
									</DragButton>
								</FocusRing>
								<LinkContentWrapper>
									<Link
										key={link.id}
										{...link}
										index={index}
										onDelete={(linkId: string) => setIsLinkToBeDeleted(linkId)}
										onUpdate={(linkId: string) => openUpdateLinkModal(linkId)}
									/>
								</LinkContentWrapper>
							</LinkWrapper>
						</DragWrapper>
					)}
				</Draggable>
			)),
		[links, formatMessage, openUpdateLinkModal, setIsLinkToBeDeleted],
	);

	const DraggableLinksView = useMemo(
		() => (
			<JSErrorBoundary
				id="externalResourcesReorder"
				packageName="jiraServicedeskExternalResourcesSettings"
				teamName="jsm-nebula"
				fallback="flag"
				onError={markReorderExperienceFailed}
			>
				<DragDropContext onDragEnd={onDragEnd}>
					<Droppable droppableId="droppable-link">
						{({ droppableProps, innerRef, placeholder }, { isDraggingOver }) => (
							<LinksListWrapper {...droppableProps} ref={innerRef} isDraggingOver={isDraggingOver}>
								{DraggableLinks}
								{placeholder}
							</LinksListWrapper>
						)}
					</Droppable>
				</DragDropContext>
			</JSErrorBoundary>
		),
		[onDragEnd, DraggableLinks],
	);

	const getContent = () => {
		if (isLoading) {
			return (
				<LinksContentContainer>
					<SpinnerContainer>
						<Spinner size="large" />
					</SpinnerContainer>
				</LinksContentContainer>
			);
		}

		if (!hasLinks) {
			return <EmptyLinkSection onAddLinkClick={openAddLinkModal} />;
		}

		return <LinksContentContainer>{DraggableLinksView}</LinksContentContainer>;
	};

	const getTruncatedLinkTitle = () =>
		getTruncatedTitleForFlag(links.find((link) => link.id === linkToBeDeleted)?.title || '');

	return (
		<LinksWrapper>
			<PageHeader
				actions={
					hasLinks ? (
						<PageActionsWrapper>
							<StyledButton
								appearance="primary"
								onClick={(_, analyticsEvent) => {
									fireUIAnalytics(analyticsEvent, 'externalResourceAdd', {
										isEmpty: false,
									});
									openAddLinkModal();
								}}
								isDisabled={isMaxLimitReached}
								name="external-resource-add"
								data-testId="servicedesk-external-links.ui.links.add-link-button"
							>
								{formatMessage(messages.externalLinksAddLinkButton)}
							</StyledButton>
							{isMaxLimitReached && <LimitReachedPopup />}
						</PageActionsWrapper>
					) : null
				}
			>
				{formatMessage(messages.externalLinksPageHeaderHeading)}
			</PageHeader>
			{hasLinks ? (
				<p data-test-id="servicedesk-external-links.ui.links.p">
					{formatMessage(messages.externalLinksReorderingPageHeaderDescription, {
						limit: MAX_NO_OF_LINKS_ALLOWED,
						a: (chunks: ReactNode) => (
							<a href={portalUrl} target="_blank">
								{chunks}
							</a>
						),
					})}
				</p>
			) : null}
			<ContextualAnalyticsData
				attributes={{
					links_count: links.length,
				}}
			>
				{getContent()}
			</ContextualAnalyticsData>
			{!isLoading && <JsmPageInteractiveEvent />}
			<DeleteDiscardPrompt
				isOpen={Boolean(linkToBeDeleted)}
				isDeleting={isDeleting}
				onCancel={() => setIsLinkToBeDeleted(null)}
				onProceed={() => {
					if (linkToBeDeleted !== null) {
						// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
						onDelete(linkToBeDeleted as string)
							.then((isSuccessfull) => {
								if (isSuccessfull) {
									setIsLinkToBeDeleted(null);
									showFlag({
										type: 'success',
										isAutoDismiss: true,
										title: messages.externalLinksDeleteSuccessFlagTitle,
										description: [
											messages.externalLinksDeleteSuccessFlagDescription,
											{
												linkTitle: getTruncatedLinkTitle(),
											},
										],
									});
								}
							})
							.catch(() => {
								showFlag({
									type: 'error',
									isAutoDismiss: true,
									title: messages.externalLinksDeleteFailureTitle,
									description: messages.externalLinksDeleteFailureDescription,
								});
							});
					}
				}}
			/>
			<AddLinkModal
				isOpen={isAddLinkModalOpen}
				onClose={closeAddLinkModal}
				mode={mode}
				// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
				link={linkToBeUpdated as ExternalLinkObject}
			/>
		</LinksWrapper>
	);
};

export default Links;
